import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ArchiveIcon from '@mui/icons-material/Archive';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { SOURCE_CATEGORIES, SOURCE_STRUCTURES } from '../../utils/callableBeerConstants';
import {
  formatIngestionRunEnumLabel,
  isExtractionRun,
} from '../../utils/brewery/beerIngestionRunFilters';
import {
  getBeerSourceCategoryChipProps,
  getBeerSourceTrustBlockReason,
} from '../../utils/brewery/beerIngestionStatus';
import { isIngestionPipeSource } from '../../utils/brewery/breweryBeerSourceFilters';

export const defaultBeerSourceFormState = {
  url: '',
  sourceCategory: 'official',
  sourceStructure: 'unknown',
  notes: '',
};

export function formatBeerSourceLabel(value) {
  return String(value || '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function BeerSourceFormPanel({
  actionError,
  actionLoading,
  editingSourceId,
  formState,
  onFormFieldChange,
  onResetForm,
  onSubmitSource,
}) {
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      <Typography variant="subtitle2">
        {editingSourceId ? 'Update classification' : 'Add source URL'}
      </Typography>
      {!editingSourceId ? (
        <TextField
          label="Source URL"
          value={formState.url}
          onChange={onFormFieldChange('url')}
          fullWidth
          required
        />
      ) : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="source-category-label">Category</InputLabel>
          <Select
            labelId="source-category-label"
            label="Category"
            value={formState.sourceCategory}
            onChange={onFormFieldChange('sourceCategory')}
          >
            {SOURCE_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {formatBeerSourceLabel(category)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="source-structure-label">Structure</InputLabel>
          <Select
            labelId="source-structure-label"
            label="Structure"
            value={formState.sourceStructure}
            onChange={onFormFieldChange('sourceStructure')}
          >
            {SOURCE_STRUCTURES.map((structure) => (
              <MenuItem key={structure} value={structure}>
                {formatBeerSourceLabel(structure)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <TextField
        label="Notes"
        value={formState.notes}
        onChange={onFormFieldChange('notes')}
        fullWidth
        multiline
        minRows={2}
      />
      <Box>
        <Button
          variant="contained"
          onClick={onSubmitSource}
          disabled={actionLoading || (!editingSourceId && !formState.url.trim())}
          startIcon={
            actionLoading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {editingSourceId ? 'Save changes' : 'Add source'}
        </Button>
        {editingSourceId ? (
          <Button sx={{ ml: 1 }} onClick={onResetForm} disabled={actionLoading}>
            Cancel edit
          </Button>
        ) : null}
      </Box>
    </Stack>
  );
}

const urlCellSx = {
  maxWidth: 280,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function formatRunTimestamp(run) {
  if (run?.completedAt?.toLocaleString) {
    return run.completedAt.toLocaleString();
  }
  if (run?.startedAt?.toLocaleString) {
    return run.startedAt.toLocaleString();
  }
  return 'unknown';
}

function isInFlightRun(run) {
  return run?.status === 'pending' || run?.status === 'running';
}

export function BeerSourceRunSummary({ latestRun }) {
  if (!latestRun) {
    return (
      <Typography variant="caption" component="span" display="block">
        No runs recorded yet.
      </Typography>
    );
  }

  const timestampLabel = formatRunTimestamp(latestRun);
  const statusLabel = formatIngestionRunEnumLabel(latestRun.status);

  if (isInFlightRun(latestRun)) {
    if (isExtractionRun(latestRun)) {
      const stageLabel = latestRun.lastCompletedStage
        ? formatIngestionRunEnumLabel(latestRun.lastCompletedStage)
        : null;
      return (
        <Typography variant="caption" component="span" display="block">
          Last run: {timestampLabel} • Extraction in progress
          {stageLabel ? ` (${stageLabel})` : ''}
        </Typography>
      );
    }
    return (
      <Typography variant="caption" component="span" display="block">
        Last run: {timestampLabel} • Import in progress
      </Typography>
    );
  }

  if (isExtractionRun(latestRun)) {
    const extractedCount = latestRun.beersExtractedCount || 0;
    if (latestRun.status === 'failure') {
      const errorHint = latestRun.errorMessage
        ? ` — ${latestRun.errorMessage.slice(0, 60)}`
        : '';
      return (
        <Typography variant="caption" component="span" display="block">
          Last run: {timestampLabel} • {statusLabel} · {extractedCount} beers extracted
          {errorHint}
        </Typography>
      );
    }
    return (
      <Typography variant="caption" component="span" display="block">
        Last run: {timestampLabel} • {statusLabel} · {extractedCount} beers extracted
      </Typography>
    );
  }

  return (
    <Typography variant="caption" component="span" display="block">
      Last run: {timestampLabel} • Added {latestRun.beersAddedCount || 0} • Updated{' '}
      {latestRun.beersUpdatedCount || 0} • Errored {latestRun.beersErroredCount || 0}
    </Typography>
  );
}

function formatCheckMeaningfulLabel(lastCheckMeaningful) {
  if (lastCheckMeaningful === true) {
    return 'meaningful';
  }
  if (lastCheckMeaningful === false) {
    return 'not meaningful';
  }
  return 'n/a';
}

export function BeerSourceCheckSummary({ sourceDoc }) {
  if (!sourceDoc?.lastCheckedAt) {
    const pendingLabel = sourceDoc?.monitorId
      ? 'No monitor checks recorded yet.'
      : 'Not monitored.';
    return (
      <Typography variant="caption" component="span" display="block">
        {pendingLabel}
      </Typography>
    );
  }

  const meaningful = formatCheckMeaningfulLabel(sourceDoc.lastCheckMeaningful);
  return (
    <Typography variant="caption" component="span" display="block">
      Last check: {sourceDoc.lastCheckedAt?.toLocaleString?.() || 'unknown'} •{' '}
      {sourceDoc.lastCheckStatus || 'unknown'} ({meaningful})
    </Typography>
  );
}

function isSafeSourceUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function isPendingSourceAction(pendingAction, sourceId, action) {
  return pendingAction?.sourceId === sourceId && pendingAction?.action === action;
}

function TrustedSourceCell({ sourceDoc, actionLoading, pendingAction, onMarkTrustedClick }) {
  const isTrusted = sourceDoc.isTrusted;
  const trustBlockReason = getBeerSourceTrustBlockReason(sourceDoc);
  const canMarkTrusted =
    isIngestionPipeSource(sourceDoc) && !isTrusted && trustBlockReason === null;
  const isTrustPending = isPendingSourceAction(pendingAction, sourceDoc.id, 'trust');

  if (isTrusted) {
    return (
      <Tooltip title="Trusted source">
        <Box
          component="span"
          sx={{ display: 'inline-flex' }}
          aria-label="Trusted source"
        >
          <StarIcon fontSize="small" color="warning" aria-hidden />
        </Box>
      </Tooltip>
    );
  }

  if (canMarkTrusted) {
    return (
      <Tooltip title="Set trusted">
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            aria-label="Set trusted"
            aria-busy={isTrustPending || undefined}
            onClick={onMarkTrustedClick}
            disabled={actionLoading}
            data-source-id={sourceDoc.id}
            sx={{ p: 0.25 }}
          >
            {isTrustPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <StarBorderIcon fontSize="small" color="disabled" aria-hidden />
            )}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  const untrustedLabel = trustBlockReason || 'Untrusted source';
  return (
    <Tooltip title={untrustedLabel}>
      <Box
        component="span"
        sx={{ display: 'inline-flex' }}
        aria-label={untrustedLabel}
      >
        <StarBorderIcon fontSize="small" color="disabled" aria-hidden />
      </Box>
    </Tooltip>
  );
}

function SourceUrlCell({ url }) {
  if (isSafeSourceUrl(url)) {
    return (
      <Link href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Link>
    );
  }
  return (
    <Typography component="span" variant="body2">
      {url}
    </Typography>
  );
}

function SourceActionIconButton({
  tooltipTitle,
  ariaLabel,
  icon,
  onClick,
  disabled = false,
  loading = false,
  color = 'default',
  buttonProps = {},
}) {
  return (
    <Tooltip title={tooltipTitle}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <IconButton
          size="small"
          color={color}
          aria-label={ariaLabel}
          aria-busy={loading || undefined}
          onClick={onClick}
          disabled={disabled || loading}
          sx={{ p: 0.25 }}
          {...buttonProps}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : icon}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

function StatusActionIconButton({
  sourceId,
  monitoringStatus,
  tooltipTitle,
  ariaLabel,
  icon,
  onStatusClick,
  actionLoading,
  loading = false,
}) {
  return (
    <SourceActionIconButton
      tooltipTitle={tooltipTitle}
      ariaLabel={ariaLabel}
      icon={icon}
      onClick={onStatusClick}
      disabled={actionLoading}
      loading={loading}
      buttonProps={{
        'data-source-id': sourceId,
        'data-monitoring-status': monitoringStatus,
      }}
    />
  );
}

function BeerSourceActions({
  sourceDoc,
  actionLoading,
  pendingAction,
  isScraping,
  onEditButtonClick,
  onStatusClick,
  onScrapeClick,
  onCheckNowClick,
  onRecreateMonitorClick,
}) {
  if (!isIngestionPipeSource(sourceDoc)) {
    return null;
  }

  const pauseButton =
    sourceDoc.monitoringStatus !== 'paused' ? (
      <StatusActionIconButton
        sourceId={sourceDoc.id}
        monitoringStatus="paused"
        tooltipTitle="Pause"
        ariaLabel="Pause"
        icon={<PauseIcon fontSize="small" />}
        onStatusClick={onStatusClick}
        actionLoading={actionLoading}
        loading={isPendingSourceAction(pendingAction, sourceDoc.id, 'pause')}
      />
    ) : null;
  const retireButton =
    sourceDoc.monitoringStatus !== 'retired' ? (
      <StatusActionIconButton
        sourceId={sourceDoc.id}
        monitoringStatus="retired"
        tooltipTitle="Retire"
        ariaLabel="Retire"
        icon={<ArchiveIcon fontSize="small" />}
        onStatusClick={onStatusClick}
        actionLoading={actionLoading}
        loading={isPendingSourceAction(pendingAction, sourceDoc.id, 'retire')}
      />
    ) : null;
  const reactivateButton =
    sourceDoc.monitoringStatus !== 'active' ? (
      <StatusActionIconButton
        sourceId={sourceDoc.id}
        monitoringStatus="active"
        tooltipTitle="Reactivate"
        ariaLabel="Reactivate"
        icon={<PlayArrowIcon fontSize="small" />}
        onStatusClick={onStatusClick}
        actionLoading={actionLoading}
        loading={isPendingSourceAction(pendingAction, sourceDoc.id, 'reactivate')}
      />
    ) : null;
  const scrapeIcon = isScraping ? (
    <CircularProgress size={16} color="inherit" />
  ) : (
    <CloudDownloadIcon fontSize="small" />
  );

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      <SourceActionIconButton
        tooltipTitle="Edit"
        ariaLabel="Edit"
        icon={<EditIcon fontSize="small" />}
        onClick={onEditButtonClick}
        disabled={actionLoading}
        buttonProps={{ 'data-source-id': sourceDoc.id }}
      />
      {pauseButton}
      {retireButton}
      {reactivateButton}
      <SourceActionIconButton
        tooltipTitle={
          sourceDoc.isTrusted
            ? 'Check now'
            : 'Trust this source before running a monitor check'
        }
        ariaLabel="Check now"
        icon={<RefreshIcon fontSize="small" />}
        onClick={onCheckNowClick}
        disabled={actionLoading || !sourceDoc.isTrusted || !sourceDoc.monitorId}
        loading={isPendingSourceAction(pendingAction, sourceDoc.id, 'check-now')}
        buttonProps={{ 'data-source-id': sourceDoc.id }}
      />
      <SourceActionIconButton
        tooltipTitle={
          sourceDoc.isTrusted
          && sourceDoc.monitorId
          && sourceDoc.monitoringStatus === 'active'
            ? 'Recreate monitor'
            : 'Trust and start monitoring before recreating a monitor'
        }
        ariaLabel="Recreate monitor"
        icon={<RestartAltIcon fontSize="small" />}
        onClick={onRecreateMonitorClick}
        disabled={
          actionLoading
          || !sourceDoc.isTrusted
          || !sourceDoc.monitorId
          || sourceDoc.monitoringStatus !== 'active'
        }
        loading={isPendingSourceAction(pendingAction, sourceDoc.id, 'recreate-monitor')}
        buttonProps={{ 'data-source-id': sourceDoc.id }}
      />
      <SourceActionIconButton
        tooltipTitle={isScraping ? 'Scraping...' : 'Scrape'}
        ariaLabel={isScraping ? 'Scraping...' : 'Scrape'}
        icon={scrapeIcon}
        onClick={onScrapeClick}
        disabled={isScraping || actionLoading}
        color="primary"
        buttonProps={{
          'data-source-url': sourceDoc.url,
          'data-source-id': sourceDoc.id,
        }}
      />
    </Stack>
  );
}

function BeerSourceTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Trusted</TableCell>
        <TableCell>URL</TableCell>
        <TableCell>Category</TableCell>
        <TableCell>Structure</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

export function BeerSourceRow({
  sourceDoc,
  actionLoading,
  pendingAction = null,
  isScraping,
  latestRun = null,
  onEditButtonClick,
  onMarkTrustedClick,
  onStatusClick,
  onScrapeClick,
  onCheckNowClick,
  onRecreateMonitorClick,
}) {
  return (
    <TableRow hover>
      <TableCell>
        <TrustedSourceCell
          sourceDoc={sourceDoc}
          actionLoading={actionLoading}
          pendingAction={pendingAction}
          onMarkTrustedClick={onMarkTrustedClick}
        />
      </TableCell>
      <TableCell sx={urlCellSx}>
        <SourceUrlCell url={sourceDoc.url} />
        <BeerSourceRunSummary latestRun={latestRun} />
        <BeerSourceCheckSummary sourceDoc={sourceDoc} />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          {...getBeerSourceCategoryChipProps(sourceDoc.sourceCategory)}
        />
      </TableCell>
      <TableCell>
        <Chip size="small" label={formatBeerSourceLabel(sourceDoc.sourceStructure)} />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={sourceDoc.monitoringStatus === 'active' ? 'success' : 'default'}
          label={formatBeerSourceLabel(sourceDoc.monitoringStatus)}
        />
      </TableCell>
      <TableCell>
        <BeerSourceActions
          sourceDoc={sourceDoc}
          actionLoading={actionLoading}
          pendingAction={pendingAction}
          isScraping={isScraping}
          onEditButtonClick={onEditButtonClick}
          onStatusClick={onStatusClick}
          onScrapeClick={onScrapeClick}
          onCheckNowClick={onCheckNowClick}
          onRecreateMonitorClick={onRecreateMonitorClick}
        />
      </TableCell>
    </TableRow>
  );
}

export function BeerSourceListPanel({
  sources,
  loading,
  error,
  latestRunsBySourceId,
  actionLoading,
  pendingAction = null,
  scrapingSourceId,
  emptyMessage,
  onEditButtonClick,
  onMarkTrustedClick,
  onStatusClick,
  onScrapeClick,
  onCheckNowClick,
  onRecreateMonitorClick,
}) {

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{String(error)}</Alert>;
  }

  if (sources.length === 0) {
    return (
      <Typography color="text.secondary">
        {emptyMessage || 'No sources configured for this brewery.'}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader aria-label="Beer sources">
        <BeerSourceTableHead />
        <TableBody>
          {sources.map((sourceDoc) => (
            <BeerSourceRow
              key={sourceDoc.id}
              sourceDoc={sourceDoc}
              actionLoading={actionLoading}
              pendingAction={pendingAction}
              isScraping={scrapingSourceId === sourceDoc.id}
              latestRun={latestRunsBySourceId?.[sourceDoc.id] ?? null}
              onEditButtonClick={onEditButtonClick}
              onMarkTrustedClick={onMarkTrustedClick}
              onStatusClick={onStatusClick}
              onScrapeClick={onScrapeClick}
              onCheckNowClick={onCheckNowClick}
              onRecreateMonitorClick={onRecreateMonitorClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

BeerSourceFormPanel.propTypes = {
  actionError: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  editingSourceId: PropTypes.string,
  formState: PropTypes.object.isRequired,
  onFormFieldChange: PropTypes.func.isRequired,
  onResetForm: PropTypes.func.isRequired,
  onSubmitSource: PropTypes.func.isRequired,
};

BeerSourceRunSummary.propTypes = {
  latestRun: PropTypes.object,
};

BeerSourceCheckSummary.propTypes = {
  sourceDoc: PropTypes.object,
};

TrustedSourceCell.propTypes = {
  sourceDoc: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingAction: PropTypes.shape({
    sourceId: PropTypes.string,
    action: PropTypes.string,
  }),
  onMarkTrustedClick: PropTypes.func.isRequired,
};

SourceUrlCell.propTypes = {
  url: PropTypes.string,
};

SourceActionIconButton.propTypes = {
  tooltipTitle: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  color: PropTypes.string,
  buttonProps: PropTypes.object,
};

StatusActionIconButton.propTypes = {
  sourceId: PropTypes.string.isRequired,
  monitoringStatus: PropTypes.string.isRequired,
  tooltipTitle: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
};

BeerSourceActions.propTypes = {
  sourceDoc: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingAction: PropTypes.shape({
    sourceId: PropTypes.string,
    action: PropTypes.string,
  }),
  isScraping: PropTypes.bool.isRequired,
  onEditButtonClick: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  onScrapeClick: PropTypes.func.isRequired,
  onCheckNowClick: PropTypes.func.isRequired,
  onRecreateMonitorClick: PropTypes.func.isRequired,
};

BeerSourceRow.propTypes = {
  sourceDoc: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingAction: PropTypes.shape({
    sourceId: PropTypes.string,
    action: PropTypes.string,
  }),
  isScraping: PropTypes.bool.isRequired,
  latestRun: PropTypes.object,
  onEditButtonClick: PropTypes.func.isRequired,
  onMarkTrustedClick: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  onScrapeClick: PropTypes.func.isRequired,
  onCheckNowClick: PropTypes.func.isRequired,
  onRecreateMonitorClick: PropTypes.func.isRequired,
};

BeerSourceListPanel.propTypes = {
  sources: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  latestRunsBySourceId: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingAction: PropTypes.shape({
    sourceId: PropTypes.string,
    action: PropTypes.string,
  }),
  scrapingSourceId: PropTypes.string,
  emptyMessage: PropTypes.string,
  onEditButtonClick: PropTypes.func.isRequired,
  onMarkTrustedClick: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  onScrapeClick: PropTypes.func.isRequired,
  onCheckNowClick: PropTypes.func.isRequired,
  onRecreateMonitorClick: PropTypes.func.isRequired,
};
